/**
 * KAN-880: кнопка "Отработано" с выбором итога: "Успех" или "Не успех".
 * Одна кнопка вместо отдельных "Успех" и "Отказ" везде (блок внимания,
 * таблица и панель доски CRM, приложение менеджера). Нажатие ничего не
 * отправляет, а раскрывает выбор (data-testid="work-outcome-menu") прямо
 * под кнопкой, в потоке (без портала: работает и внутри модальной панели).
 * Раскрывающийся блок (disclosure): кнопка с aria-expanded/aria-controls,
 * при открытии фокус на первом варианте; выбор зовёт onChoose и закрывает
 * блок, Esc и клик мимо закрывают без выбора; фокус возвращается на кнопку.
 * Пока блок открыт, у корня стоит WORK_OUTCOME_OPEN_ATTR (lib/workActions): панель
 * (Radix Sheet ловит Esc раньше нас) по isInsideOpenWorkOutcome не закрывается.
 * compact: кнопка-иконка (подпись в aria-label и title) для таблицы CRM.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { CheckCheck, ThumbsDown, ThumbsUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  WORK_DONE_LABEL,
  WORK_FAILURE_LABEL,
  WORK_OUTCOME_OPEN_ATTR,
  WORK_SUCCESS_LABEL,
} from '@/lib/workActions'

export type WorkOutcome = 'success' | 'refused'

const OPTIONS: readonly { value: WorkOutcome; label: string; icon: typeof ThumbsUp; className: string }[] = [
  { value: 'success', label: WORK_SUCCESS_LABEL, icon: ThumbsUp, className: 'text-emerald-700' },
  { value: 'refused', label: WORK_FAILURE_LABEL, icon: ThumbsDown, className: 'text-rose-700' },
]

export interface WorkOutcomeChoiceProps {
  disabled?: boolean
  onChoose: (outcome: WorkOutcome) => void
  /** Какие итоги предлагать (по умолчанию оба). */
  outcomes?: readonly WorkOutcome[]
  compact?: boolean
  /** Свой вид кнопки и пунктов (крупные кнопки приложения); иначе кнопки shadcn. */
  triggerClassName?: string
  optionClassName?: string
  /** Раскладка выбора (по умолчанию в строку рядом с кнопкой). */
  menuClassName?: string
  className?: string
  /** KAN-965: низкие кнопки с подписью (h-8, мелкий текст) для строки внимания. */
  dense?: boolean
}

const DENSE_BUTTON = 'h-8 px-2 text-xs'

export function WorkOutcomeChoice({
  disabled = false,
  onChoose,
  outcomes = ['success', 'refused'],
  compact = false,
  triggerClassName,
  optionClassName,
  menuClassName,
  className,
  dense = false,
}: WorkOutcomeChoiceProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const firstOptionRef = useRef<HTMLButtonElement | null>(null)
  const menuId = useId()

  const close = (returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return undefined
    firstOptionRef.current?.focus()
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  const choose = (outcome: WorkOutcome) => {
    close(true)
    onChoose(outcome)
  }
  const options = OPTIONS.filter((o) => outcomes.includes(o.value))
  const triggerProps = {
    ref: triggerRef,
    type: 'button' as const,
    disabled,
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: () => setOpen((prev) => !prev),
  }
  const rootAttrs = open ? { [WORK_OUTCOME_OPEN_ATTR]: '' } : {}

  return (
    <div ref={rootRef} {...rootAttrs} className={cn('flex gap-1', compact ? 'flex-nowrap' : 'flex-wrap', className)}>
      {triggerClassName ? (
        <button {...triggerProps} className={triggerClassName}>
          {WORK_DONE_LABEL}
        </button>
      ) : (
        <Button
          {...triggerProps}
          size="sm"
          variant="outline"
          className={compact ? 'h-7 w-7 p-0' : dense ? DENSE_BUTTON : undefined}
          title={compact ? WORK_DONE_LABEL : undefined}
          aria-label={compact ? WORK_DONE_LABEL : undefined}
        >
          <CheckCheck className={compact ? 'h-3.5 w-3.5' : 'mr-1 h-3.5 w-3.5'} aria-hidden="true" />
          {!compact && WORK_DONE_LABEL}
        </Button>
      )}
      {open && (
        <div
          id={menuId}
          role="group"
          aria-label={WORK_DONE_LABEL}
          data-testid="work-outcome-menu"
          className={menuClassName ?? cn('flex gap-1', compact ? 'flex-nowrap' : 'flex-wrap')}
        >
          {options.map(({ value, label, icon: Icon, className: tone }, index) => {
            const common = {
              ref: index === 0 ? firstOptionRef : undefined,
              type: 'button' as const,
              'data-testid': `work-outcome-${value}`,
              onClick: () => choose(value),
            }
            return optionClassName ? (
              <button key={value} {...common} className={cn(optionClassName, tone)}>
                {label}
              </button>
            ) : (
              <Button key={value} {...common} size="sm" variant="outline" className={cn(compact && 'h-7 px-2 text-xs', dense && !compact && DENSE_BUTTON, tone)}>
                <Icon className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                {label}
              </Button>
            )
          })}
        </div>
      )}
    </div>
  )
}
